import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  deleteAdminAccessUiModal,
  getAdminAccessUiModals,
  upsertAdminAccessUiModal,
  type AccessUiModalRecord,
} from "../api/adminAccessUiModalsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";

const PRESETS: AccessUiModalRecord[] = [
  {
    reason: "next_chapter_quota",
    title: "Premium kotan azalacak",
    message: "Sonraki bölüme geçersen günlük kotandan düşecek. Devam etmek istediğine emin misin?",
    confirmLabel: "Devam et",
    cancelLabel: "İptal",
    showCancel: true,
    isBlocking: true,
    primaryCtaLabel: "Paketleri incele",
    primaryCtaUrl: "/packages",
  },
  {
    reason: "consume_confirm",
    title: "Okumayı başlat",
    message: "Bu bölümü okursan haklarından biri düşer. Devam etmek istiyor musun?",
    confirmLabel: "Okumayı başlat",
    cancelLabel: "Daha sonra",
    showCancel: true,
    isBlocking: true,
  },
  {
    reason: "single_unlock_confirm",
    title: "Tekli açma",
    message: "Bu bölümü tek seferlik açmak için Z token harcayacaksın. Onaylıyor musun?",
    confirmLabel: "Tekli aç",
    cancelLabel: "Vazgeç",
    showCancel: true,
    isBlocking: true,
  },
  {
    reason: "balance_insufficient",
    title: "Bakiyen yetersiz",
    message: "Tekli açma için yeterli Z token yok. Token yükleyebilir veya paket satın alabilirsin.",
    confirmLabel: "Token yükle",
    cancelLabel: "Kapat",
    showCancel: true,
    isBlocking: false,
    primaryCtaLabel: "Token Satın Al",
    primaryCtaUrl: "/wallet",
    secondaryCtaLabel: "Paketleri Gör",
    secondaryCtaUrl: "/packages",
  },
];

export default function AdminAccessUiModals() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Economy.View");
  const { allowed: canEdit } = usePermissionCheck("Economy.Settings");
  const [items, setItems] = useState<AccessUiModalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<AccessUiModalRecord | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const list = await getAdminAccessUiModals();
      if (!active) return;
      setItems(list);
      setLoading(false);
    };
    if (allowed) {
      load();
    } else {
      setLoading(false);
    }
    return () => {
      active = false;
    };
  }, [allowed]);

  const existingReasons = useMemo(() => new Set(items.map((item) => item.reason)), [items]);

  const openEditor = (record?: AccessUiModalRecord) => {
    setDraft(
      record
        ? {
            ...record,
          }
        : {
            reason: "",
            title: "",
            message: "",
            confirmLabel: "",
            cancelLabel: "",
            showCancel: true,
            isBlocking: true,
          },
    );
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.reason.trim()) {
      Swal.fire("Geçersiz", "Reason boş olamaz", "warning");
      return;
    }
    if (!canEdit) {
      Swal.fire("Yetki yok", "Bu alanı güncelleme iznin yok", "warning");
      return;
    }
    const res = await upsertAdminAccessUiModal(draft);
    if (res.success) {
      Swal.fire("Kaydedildi", `${draft.reason} güncellendi`, "success");
      setDraft(null);
      const list = await getAdminAccessUiModals();
      setItems(list);
    } else {
      Swal.fire("Hata", "Kayıt sırasında sorun oluştu", "error");
    }
  };

  const handleDelete = async (reason: string) => {
    if (!canEdit) {
      Swal.fire("Yetki yok", "Silme yetkin yok", "warning");
      return;
    }
    const confirm = await Swal.fire({
      title: "Silinsin mi?",
      text: `${reason} modali kaldırılsın mı?`,
      showCancelButton: true,
      confirmButtonText: "Evet",
      cancelButtonText: "Vazgeç",
      icon: "warning",
    });
    if (!confirm.isConfirmed) return;
    const res = await deleteAdminAccessUiModal(reason);
    if (res.success) {
      Swal.fire("Silindi", `${reason} kaldırıldı`, "success");
      setItems((prev) => prev.filter((item) => item.reason !== reason));
    } else {
      Swal.fire("Hata", "Silme başarısız", "error");
    }
  };

  if (checkingPermission) {
    return <div className="rounded-lg bg-white p-6 text-center text-sm text-slate-500">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="rounded-lg bg-white p-6 text-center text-sm text-red-500">Bu sayfaya erişimin yok.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl bg-white p-6 shadow">
        <h1 className="text-xl font-semibold text-slate-900">Access UI Modalları</h1>
        <p className="mt-2 text-sm text-slate-500">Reader akışında gösterilecek modal içeriklerini yönetin.</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {PRESETS.map((preset) => (
            <button
              key={preset.reason}
              type="button"
              onClick={() => openEditor(existingReasons.has(preset.reason) ? items.find((i) => i.reason === preset.reason) ?? preset : preset)}
              className="rounded-full border border-slate-200 px-3 py-1 text-slate-600 hover:bg-slate-100"
            >
              {preset.reason}
            </button>
          ))}
          <button type="button" onClick={() => openEditor()} className="rounded-full border border-emerald-200 px-3 py-1 text-emerald-700">
            Yeni modal
          </button>
        </div>
      </header>

      {loading ? (
        <div className="rounded-3xl bg-white p-6 text-center text-sm text-slate-500">Yükleniyor...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((item) => (
            <article key={item.reason} className="rounded-3xl bg-white p-5 shadow">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400">{item.reason}</p>
                  <h2 className="text-base font-semibold text-slate-900">{item.title}</h2>
                  <p className="mt-1 text-sm text-slate-600">{item.message}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button className="text-xs text-blue-600" onClick={() => openEditor(item)}>
                    Düzenle
                  </button>
                  <button className="text-xs text-red-500" onClick={() => handleDelete(item.reason)}>
                    Sil
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Onay: {item.confirmLabel ?? "Devam et"} {item.showCancel ? `? ?ptal: ${item.cancelLabel || "?ptal"}` : ""}
              </div>
            </article>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 text-slate-900">
            <h2 className="text-lg font-semibold">Modal düzenle</h2>
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                Reason
                <input
                  type="text"
                  value={draft.reason}
                  onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  disabled={Boolean(draft.id)}
                />
              </label>
              <label className="block text-sm">
                Başlık
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                Mesaj
                <textarea
                  value={draft.message}
                  onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                  className="mt-1 h-24 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block text-sm">
                  Onay butonu
                  <input
                    type="text"
                    value={draft.confirmLabel ?? ""}
                    onChange={(e) => setDraft({ ...draft, confirmLabel: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  İptal butonu
                  <input
                    type="text"
                    value={draft.cancelLabel ?? ""}
                    onChange={(e) => setDraft({ ...draft, cancelLabel: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.showCancel}
                    onChange={(e) => setDraft({ ...draft, showCancel: e.target.checked })}
                  />
                  İptal butonu göster
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.isBlocking}
                    onChange={(e) => setDraft({ ...draft, isBlocking: e.target.checked })}
                  />
                  Onay gerekir
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block text-sm">
                  Birincil CTA label
                  <input
                    type="text"
                    value={draft.primaryCtaLabel ?? ""}
                    onChange={(e) => setDraft({ ...draft, primaryCtaLabel: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  Birincil CTA URL
                  <input
                    type="text"
                    value={draft.primaryCtaUrl ?? ""}
                    onChange={(e) => setDraft({ ...draft, primaryCtaUrl: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  İkincil CTA label
                  <input
                    type="text"
                    value={draft.secondaryCtaLabel ?? ""}
                    onChange={(e) => setDraft({ ...draft, secondaryCtaLabel: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  İkincil CTA URL
                  <input
                    type="text"
                    value={draft.secondaryCtaUrl ?? ""}
                    onChange={(e) => setDraft({ ...draft, secondaryCtaUrl: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDraft(null)} className="rounded border border-slate-200 px-4 py-2 text-sm">
                Vazgeç
              </button>
              <button onClick={handleSave} className="rounded bg-emerald-600 px-4 py-2 text-sm text-white">
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
